import type { z } from 'zod';
import { fraction } from '../common/index.js';
import type * as E from '../entities/index.js';
import { MENTION_ESTIMATION } from '../entities/index.js';

/**
 * Jeu « Famille Bélénus » (F06), entièrement fictif : il reprend les données des maquettes
 * (build-kit/06-demo-vendredi/donnees/belenus.json). Téléphones dans la plage fictive, e-mails en .test.
 * Correction n° 1 de la synthèse : Marie a survécu à ses parents, Marc et Sandra héritent par TRANSMISSION.
 */

type T<S extends z.ZodType> = z.infer<S>;

export const uuid = (n: number): string => `0192a6c0-0000-7000-8000-${n.toString(16).padStart(12, '0')}`;

export const INSTANT = '2026-10-14T09:00:00-04:00';

export const IDS = {
  dossier: uuid(0xd01),
  dossierDemosthene: uuid(0xd02),
  etude: uuid(0xe01),
  albert: uuid(0x101),
  rose: uuid(0x102),
  josiane: uuid(0x103),
  lucien: uuid(0x104),
  marie: uuid(0x105),
  helene: uuid(0x106),
  marc: uuid(0x107),
  sandra: uuid(0x108),
  filleLucien: uuid(0x109),
  succAlbert: uuid(0x201),
  succRose: uuid(0x202),
  succMarie: uuid(0x203),
  succLucien: uuid(0x204),
  terrain: uuid(0x301),
  isabelle: uuid(0x401),
  brigitte: uuid(0x402),
  cynthia: uuid(0x403),
  waBrigitte: uuid(0x501),
  waIsabelle: uuid(0x502),
  emailCabinet: uuid(0x503),
  fil: uuid(0x601),
  message: uuid(0x602),
  sortant: uuid(0x603),
  plan: uuid(0x701),
  mandat: uuid(0x801),
  devis: uuid(0x802),
  documentActe: uuid(0x901),
  exigence: uuid(0x902),
  etape: uuid(0xa01),
  tache: uuid(0xa02),
  decision: uuid(0xa03),
  echeance: uuid(0xa04),
  interrogation: uuid(0xa05),
  renonciation: uuid(0xa06),
  note: uuid(0xa07),
  sensible: uuid(0xa08),
  consentement: uuid(0xa09),
  facture: uuid(0xa0a),
  paiement: uuid(0xa0b),
  echeanceMarc1: uuid(0xa0c),
  echeanceMarc2: uuid(0xa0d),
  lien: uuid(0xa0e),
  impersonation: uuid(0xa0f),
  journal: uuid(0xa10),
  parametre: uuid(0xa11),
  relation: uuid(0xa12),
  rgpd: uuid(0xa13),
  export: uuid(0xa14),
  compteRendu: uuid(0xa15),
  acteNotarie: uuid(0xa17),
  contactImporte: uuid(0xa18),
  video: uuid(0xa19),
  compteMarc: uuid(0x404),
  supplement: uuid(0xa20),
  devisDetaille: uuid(0xb01),
  ligneDevis: uuid(0xb02),
} as const;

const euros = (e: number) => ({ amountCents: Math.round(e * 100), currency: 'EUR' as const });

const prefs = (canal: 'whatsapp' | 'email' | null, seulementCabinet = false): T<typeof E.ContactPreferencesSchema> => ({
  preferredChannel: canal,
  contactOnlyByCabinet: seulementCabinet,
  delegatedContactUserId: null,
  language: 'fr',
});

const naissance = (value: string | null, precision: 'jour' | 'annee' | 'inconnue' = 'jour') => ({ value, precision });

const personne = (
  id: string,
  prenoms: string,
  decede: string | null,
  role: T<typeof E.PersonRoleSchema>,
  lien: string | null,
  extra: Partial<T<typeof E.PersonStaffViewSchema>> = {},
): T<typeof E.PersonStaffViewSchema> => ({
  id,
  dossierId: IDS.dossier,
  firstNames: prenoms,
  lastName: 'B.',
  birthName: null,
  sex: null,
  birthDate: naissance(null, 'inconnue'),
  birthPlace: null,
  deathDate: decede,
  deathPlace: decede ? 'Grand-Bourg' : null,
  vitalStatus: decede ? 'decede' : 'vivant',
  isHypothesis: false,
  relationLabel: lien,
  role,
  reachability: 'joignable',
  contacts: [],
  preferences: prefs('whatsapp'),
  sensitiveLabels: [],
  rowVersion: 1,
  ...extra,
});

const contact = (id: number, type: 'phone' | 'whatsapp' | 'email', value: string, masque: boolean): T<typeof E.ContactStaffViewSchema> => ({
  id: uuid(id),
  type,
  value,
  visibility: masque ? 'equipe' : 'famille',
  maskedForFamily: masque,
  isPreferred: true,
  timezone: type === 'email' ? null : 'America/Guadeloupe',
});

const personnes: T<typeof E.PersonStaffViewSchema>[] = [
  personne(IDS.albert, 'Albert', '2009-03-14', 'auteur', 'défunt', {
    sex: 'M',
    birthDate: naissance('1928-06-02'),
    birthPlace: 'Grand-Bourg',
  }),
  personne(IDS.rose, 'Rose', '2011-08-30', 'conjoint', 'épouse du défunt', {
    sex: 'F',
    birthDate: naissance('1931-01-19'),
    birthPlace: 'Grand-Bourg',
  }),
  personne(IDS.josiane, 'Josiane Marie', null, 'mandant', 'fille', {
    sex: 'F',
    birthDate: naissance('1956-03-12'),
    birthPlace: 'Grand-Bourg',
    contacts: [contact(0x1031, 'whatsapp', '+590690000101', false)],
  }),
  personne(IDS.lucien, 'Lucien', '2026-05-02', 'heritier', 'fils', {
    sex: 'M',
    birthDate: naissance('1959-10-04'),
    birthPlace: 'Grand-Bourg',
  }),
  personne(IDS.marie, 'Marie', '2019-03-08', 'heritier', 'fille', { sex: 'F', deathPlace: 'Pointe-à-Pitre' }),
  personne(IDS.helene, 'Hélène', null, 'conjoint', 'veuve du fils', {
    lastName: 'T.',
    sex: 'F',
    reachability: 'a_relancer',
    contacts: [contact(0x1061, 'whatsapp', '+590690000106', true)],
    preferences: prefs('whatsapp', true),
    sensitiveLabels: [{ id: IDS.sensible, label: 'Deuil récent, ne contacter que par le cabinet', sharedOutsideTeam: false }],
  }),
  personne(IDS.marc, 'Marc', null, 'heritier', 'petit-fils', {
    sex: 'M',
    birthDate: naissance('1984-07-21'),
    birthPlace: 'Les Abymes',
    contacts: [contact(0x1071, 'email', 'marc.b@exemple.test', false), contact(0x1072, 'whatsapp', '+590690000107', false)],
    preferences: prefs('email'),
  }),
  personne(IDS.sandra, 'Sandra', null, 'heritier', 'petite-fille', {
    sex: 'F',
    contacts: [contact(0x1081, 'email', 'sandra.b@exemple.test', true), contact(0x1082, 'phone', '+33639980012', true)],
    preferences: prefs('email'),
  }),
  personne(IDS.filleLucien, '', null, 'heritier', 'fille de Lucien (prénom inconnu)', {
    isHypothesis: true,
    vitalStatus: 'inconnu',
    reachability: 'injoignable',
    preferences: { ...prefs(null), language: 'ja' },
  }),
];

const succession = (id: string, defunt: string, ouverture: string): T<typeof E.SuccessionSchema> => ({
  id,
  deceasedPersonId: defunt,
  openedAt: ouverture,
  deathInDomicileDepartment: true,
  hasDispositions: false,
  status: 'ouverte',
});

const part = (personId: string, n: number, d: number, mode: T<typeof E.ModeDevolutionSchema>, via: string | null = null) => ({
  personId,
  successionId: IDS.succAlbert,
  share: fraction(n, d),
  mode,
  viaPersonId: via,
});

/** Succession d'Albert : exemple de maquette, à valider notaire (le moteur et ses cas d'or sont L10 et L03). */
const devolution: T<typeof E.DevolutionResultSchema> = {
  successionId: IDS.succAlbert,
  shares: [
    part(IDS.josiane, 1, 3, 'propre'),
    part(IDS.marc, 1, 6, 'transmission', IDS.marie),
    part(IDS.sandra, 1, 6, 'transmission', IDS.marie),
  ],
  reserves: [
    {
      successionId: IDS.succAlbert,
      branchLabel: 'Branche de Lucien (succession de Lucien : conjointe à interroger, fille au Japon non identifiée)',
      share: fraction(1, 3),
      reason: 'Lucien a survécu à son père : sa part passe par sa propre succession, encore ouverte.',
    },
  ],
  explanation: [
    'Albert laisse trois enfants : Josiane, Lucien et Marie, chacun pour un tiers (C. civ. 734 et 735).',
    'Marie, décédée en 2019 après son père, transmet son tiers dans sa propre succession à Marc et Sandra (transmission, pas représentation).',
    'Lucien, décédé en 2026 après son père, transmet son tiers dans sa propre succession : part mise en réserve.',
    'Option de Rose, conjointe survivante : non exercée ; les deux scénarios sont affichés.',
    'Exemple de maquette : résultat à valider par la clerc ou la notaire.',
  ],
  status: 'a_valider_notaire',
  engineVersion: 'maquette-1',
  computedAt: INSTANT,
  spouseOption: {
    spousePersonId: IDS.rose,
    successionId: IDS.succAlbert,
    status: 'non_exercee',
    allChildrenCommon: true,
    scenarios: [
      { option: 'usufruit_total', shares: [] },
      { option: 'quart_pleine_propriete', shares: [{ ...part(IDS.rose, 1, 4, 'propre') }] },
    ],
  },
};

const etapes: T<typeof E.StepSchema>[] = [
  {
    id: IDS.etape,
    phase: 'P3',
    label: 'Généalogie : vivants et défunts',
    orderIndex: 3,
    status: 'en_cours',
    skippedReason: null,
    dueAt: null,
  },
  {
    id: uuid(0xa21),
    phase: 'P6',
    label: 'Déclaration de succession',
    orderIndex: 6,
    status: 'saute',
    skippedReason: 'Décès d’Albert de plus de 6 ans (BETA-02)',
    dueAt: null,
  },
];

const echeance: T<typeof E.DeadlineSchema> = {
  id: IDS.echeance,
  dossierId: IDS.dossier,
  successionId: IDS.succLucien,
  kind: 'declaration_succession',
  label: 'Déclaration de succession de Lucien',
  dueAt: '2026-11-02',
  status: 'proche',
  parameterKeys: ['declaration.delai_mois.domicile'],
};

const resume: T<typeof E.DossierSummarySchema> = {
  id: IDS.dossier,
  reference: 'BEL-2026-001',
  title: 'Succession Bélénus',
  status: 'actif',
  currentPhase: 'P3',
  blocker: { kind: 'heritier_injoignable', label: 'Branche Japon injoignable', since: '2026-09-30' },
  estimatedValue: { low: euros(150000), high: euros(210000) },
  etudeName: 'Étude principale',
  heirsCount: 11,
  respondedCount: 7,
  toFollowUpCount: 3,
  unreachableCount: 1,
};

const dossier: T<typeof E.DossierStaffViewSchema> = {
  ...resume,
  mandantPersonId: IDS.josiane,
  mandateStatus: 'signe',
  nextAction: 'Relancer Hélène T. par appel (Brigitte)',
  completeness: 82,
  steps: etapes,
  deadlines: [echeance],
  closedAt: null,
  externalAccessUntil: null,
  rowVersion: 7,
};

const terrain: T<typeof E.AssetSchema> = {
  id: IDS.terrain,
  dossierId: IDS.dossier,
  kind: 'terrain',
  label: 'Terrain de Grand-Bourg',
  cadastralRef: 'AB 0123',
  commune: 'Grand-Bourg',
  areaM2: 6000,
  pluZoning: 'agricole',
  occupancy: 'vacant',
  titleStatus: 'trouve',
  valuation: { low: euros(150000), high: euros(210000), source: 'comparables' },
};

export const BELENUS = {
  ids: IDS,
  dossier,
  resume,
  personnes,
  successions: [
    succession(IDS.succAlbert, IDS.albert, '2009-03-14'),
    succession(IDS.succRose, IDS.rose, '2011-08-30'),
    succession(IDS.succMarie, IDS.marie, '2019-03-08'),
    succession(IDS.succLucien, IDS.lucien, '2026-05-02'),
  ],
  relations: [
    [IDS.albert, IDS.rose, 'union'],
    [IDS.albert, IDS.josiane, 'filiation'],
    [IDS.albert, IDS.lucien, 'filiation'],
    [IDS.albert, IDS.marie, 'filiation'],
    [IDS.rose, IDS.josiane, 'filiation'],
    [IDS.rose, IDS.lucien, 'filiation'],
    [IDS.rose, IDS.marie, 'filiation'],
    [IDS.lucien, IDS.helene, 'union'],
    [IDS.lucien, IDS.filleLucien, 'filiation'],
    [IDS.marie, IDS.marc, 'filiation'],
    [IDS.marie, IDS.sandra, 'filiation'],
  ] as const,
  devolution,
  etapes,
  echeance,
  terrain,
  estimation: {
    dossierId: IDS.dossier,
    scenario: 'vendre',
    mention: MENTION_ESTIMATION,
    visibility: 'equipe',
    perHeir: [
      { personId: IDS.josiane, displayName: 'Josiane B.', share: fraction(1, 3), low: euros(48000), high: euros(68000) },
      { personId: IDS.marc, displayName: 'Marc B.', share: fraction(1, 6), low: euros(24000), high: euros(34000) },
      { personId: IDS.sandra, displayName: 'Sandra B.', share: fraction(1, 6), low: euros(24000), high: euros(34000) },
    ],
    reserve: euros(48000),
    fees: euros(6000),
    reliability: {
      score: 62,
      factors: [
        { label: 'Titre trouvé (acte de vente 1974)', impact: 'positif' },
        { label: 'Branche de Lucien incomplète', impact: 'negatif' },
        { label: 'Valeur par ventes comparables, sans expertise', impact: 'neutre' },
      ],
    },
    computedAt: INSTANT,
  } satisfies T<typeof E.EstimateSchema>,
} as const;
